
import { v4 as uuidv4 } from 'uuid';
import { BaseTool } from './BaseTool';
import { ThreeEvent } from '@react-three/fiber';
import * as THREE from 'three';
import { MassElement } from '../core/types';
import { buildMassFloors } from './site/MassingSiteTools';
import i18n from '../i18n';

export { type MassElement } from '../core/types';
export type { MassGeometryData } from '../core/types';

export class InPlaceMassTool extends BaseTool {
    id = 'mass';
    name = 'In-Place Mass';
    cursor = 'crosshair';

    private points: THREE.Vector3[] = [];
    private currentPoint: THREE.Vector3 | null = null;

    protected onActivate(): void {
        this.notify(i18n.t('tools.notify.inPlaceMassSketchTheMass'), 'info');
    }

    onPointerDown(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        if (event.button !== 0) return;

        const point = this.getIntersection(event);
        if (!point) return;

        const snapped = this.snapToGrid(point);
        this.points.push(snapped);

        // Check if closed
        if (this.points.length > 2) {
            const start = this.points[0];
            if (snapped.distanceTo(start) < 0.2) {
                this.points.pop();
                this.finishSketch();
                return;
            }
        }
    }

    onPointerMove(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        const point = this.getIntersection(event);
        if (point) {
            this.currentPoint = this.snapToGrid(point);
            this.updatePreview();
        }
    }

    onPointerUp(): void { }

    onKeyDown(event: KeyboardEvent): void {
        if (event.key === 'Enter') {
            if (this.points.length >= 3) {
                this.finishSketch();
            }
        } else if (event.key === 'Escape') {
            if (this.points.length > 0) {
                this.points = [];
                this.updatePreview();
            } else {
                super.onKeyDown(event);
            }
        }
    }

    private updatePreview() {
        if (!this.context) return;

        if (this.points.length > 0 && this.currentPoint) {
            const previewPoints = [...this.points, this.currentPoint];
            this.context.appStore.setToolPreview({
                type: 'polygon',
                points: previewPoints,
                closed: false,
                color: '#880088' // Mass Purple
            });
        } else {
            this.context.appStore.setToolPreview(null);
        }
    }

    private finishSketch() {
        if (!this.context) return;

        const footprint: [number, number][] = this.points.map(p => [p.x, p.y]);
        const props = this.context.bimStore.toolProperties;
        const height = props.massHeight ?? 10;
        const spacing = props.massFloorSpacing ?? 4;
        const area = calculatePolygonArea(footprint);
        const floors = buildMassFloors(area, height, spacing);

        const mass: MassElement = {
            id: uuidv4(),
            category: 'Mass',
            type: 'Mass: Generic Shape',
            name: 'Mass In-Place',
            parameters: {
                'Height': `${height} m`,
                'Area': area.toFixed(2) + ' m²',
                'Volume': `${(area * height).toFixed(1)} m³`,
                'Floors': String(floors.length),
                'Family and Type': 'Generic Mass: In-Place',
            },
            constraints: [],
            geometryData: {
                footprint,
                height,
                elevation: 0, // Base level
                floors,
                formType: 'Extrusion',
                isClosed: true,
            }
        };

        this.context.bimStore.addElement(mass);
        this.notify(i18n.t('tools.notify.inPlaceMassCreated', { area: area.toFixed(1), height: height }), 'success');

        this.points = [];
        this.context.appStore.setToolPreview(null);
    }
}

function calculatePolygonArea(points: [number, number][]): number {
    let area = 0;
    const n = points.length;
    for (let i = 0; i < n; i++) {
        const j = (i + 1) % n;
        area += points[i][0] * points[j][1];
        area -= points[j][0] * points[i][1];
    }
    return Math.abs(area) / 2;
}
